// Import React
import React from "react";

// Import css
import "./AiModelsInfoTable.css";



// Reusable rating bar for score columns
const RatingBar = ({ value }) => {

    // Keep value safely between 0 and 10
    const safeValue = Math.max(0, Math.min(10, value || 0));

    // Convert rating into a percentage for the fill bar
    const percentage = `${safeValue * 10}%`;

    // Return the visual rating bar
    return (

        <div className="ai-models-info-table__rating-wrap">

            <div className="ai-models-info-table__rating-bar">

                <div
                    className="ai-models-info-table__rating-fill"
                    style={{ width: percentage }}
                />

            </div>

            <span className="ai-models-info-table__rating-text">

                {safeValue}/10

            </span>

        </div>

    );

};



// Small yes/no pill
const FeaturePill = ({ enabled, trueLabel = "✅", falseLabel = "❌" }) => {

    // Return the pill display
    return (

        <span
            className={`ai-models-info-table__pill ${
                enabled
                    ? "ai-models-info-table__pill--yes"
                    : "ai-models-info-table__pill--no"
            }`}
        >

            {enabled ? trueLabel : falseLabel}

        </span>

    );

};



// Single row card for mobile layout
const MobileModelCard = ({ model }) => {

    // Return card layout for smaller screens
    return (

        <article className="ai-models-info-table__card">

            <div className="ai-models-info-table__card-head">

                <div>

                    <h3 className="ai-models-info-table__card-title">
                        {model.name}
                    </h3>

                    <p className="ai-models-info-table__card-description">
                        {model.description}
                    </p>

                </div>

                <FeaturePill
                    enabled={model.supportsText}
                    trueLabel="✅ Text"
                    falseLabel="❌ Text"
                />

            </div>

            <div className="ai-models-info-table__card-grid">

                <div className="ai-models-info-table__metric">

                    <span className="ai-models-info-table__metric-label">

                        Speed

                    </span>

                    <RatingBar value={model.speed} />

                </div>

                <div className="ai-models-info-table__metric">

                    <span className="ai-models-info-table__metric-label">

                        Quality

                    </span>
                    
                    <RatingBar value={model.quality} />

                </div>

            </div>

            <div className="ai-models-info-table__notes">

                <div className="ai-models-info-table__note">

                    <span className="ai-models-info-table__note-label">

                        Best for

                    </span>

                    <span className="ai-models-info-table__note-text">

                        {model.bestFor}

                    </span>

                </div>

                {/* 
                <div className="ai-models-info-table__note">

                    <span className="ai-models-info-table__note-label">

                        Watch out for

                    </span>

                    <span className="ai-models-info-table__note-text">

                        {model.drawback}

                    </span>

                </div>
                */}

            </div>

        </article>

    );

};



// Keep model data outside the component so it stays reusable
const models = [
    {
        id: "flux",
        name: "Flux",
        description: "Fast, lightweight image model built for quick generation with solid overall quality.",
        speed: 5,
        quality: 10,
        supportsText: true,
        bestFor: "Fast everyday image generation",
        //drawback: "Less detailed than premium quality-focused models",
    },
    {
        id: "juggernaut",
        name: "Juggernaut",
        description: "High-detail, versatile model known for strong realism and stylised artwork with rich textures and visual depth.",
        speed: 6,
        quality: 9,
        supportsText: false,
        bestFor: "Detailed, rich, versatile artwork",
        //drawback: "Slower than speed-first models",
    },
    {
        id: "realism",
        name: "Realism",
        description: "Photorealistic model focused on natural lighting, believable detail, and lifelike photographic results.",
        speed: 8,
        quality: 8,
        supportsText: false,
        bestFor: "Photorealistic scenes and product-style imagery",
        //drawback: "Less suited to highly stylised outputs",
    },
    {
        id: "lightning",
        name: "Lightning",
        description: "Ultra-fast model designed for speed-first generation when quick turnaround matters more than fine detail.",
        speed: 9,
        quality: 6,
        supportsText: false,
        bestFor: "Rapid drafts and quick iteration",
        //drawback: "Can lose detail and polish compared with slower models",
    },
    {
        id: "z-image",
        name: "Z-Image",
        description: "Speed-optimised model that balances fast output with dependable quality.",
        speed: 7,
        quality: 7,
        supportsText: true,
        bestFor: "Speed with accurate text rendering",
        //drawback: "Not as detailed as the top-end realism models",
    },
    {
        id: "flux-2",
        name: "Flux 2",
        description: "The newest version of a fast, high quality model.",
        speed: 10,
        quality: 8,
        supportsText: false,
        bestFor: "High speed and quality",
    },
];



// Main component
const AiModelsInfoTable = ({ aiModels = [] }) => {

    // Use incoming data if present, otherwise use fallback demo data
    const dataToRender = aiModels.length > 0 ? aiModels : models;

    // Return the comparison UI
    return (

        <div className="ai-models-info-table">

            <div className="ai-models-info-table__desktop">

                <div className="ai-models-info-table__grid-header">

                    <div className="ai-models-info-table__head-cell">
                        Model  
                    </div>

                    <div className="ai-models-info-table__head-cell">
                        Speed
                    </div>

                    <div className="ai-models-info-table__head-cell">
                        Quality
                    </div>

                    <div className="ai-models-info-table__head-cell ai-models-info-table__head-cell--center ">
                        Text
                    </div>

                    <div className="ai-models-info-table__head-cell">
                        Best for
                    </div>

                    {/* 
                    <div className="ai-models-info-table__head-cell">
                        Watch out for
                    </div>
                    */}

                </div>

                <div className="ai-models-info-table__grid-body">

                    {dataToRender.map((model) => (

                        <React.Fragment key={model.id}>

                            <div className="ai-models-info-table__cell ai-models-info-table__cell--model">

                                <div className="ai-models-info-table__model-name-wrap">

                                    <span className="ai-models-info-table__model-name">
                                        {model.name}
                                    </span>

                                </div>

                            </div>

                            <div className="ai-models-info-table__cell">

                                <RatingBar value={model.speed} />

                            </div>

                            <div className="ai-models-info-table__cell">

                                <RatingBar value={model.quality} />

                            </div>

                            <div className="ai-models-info-table__cell ai-models-info-table__cell--center">

                                <FeaturePill enabled={model.supportsText} />

                            </div>

                            <div className="ai-models-info-table__cell ai-models-info-table__cell--wrap">

                                <span className="ai-models-info-table__plain-text">
                                    {model.bestFor}
                                </span>

                            </div>

                            {/* 
                            <div className="ai-models-info-table__cell ai-models-info-table__cell--wrap">

                                <span className="ai-models-info-table__plain-text">
                                    {model.drawback}
                                </span>

                            </div>
                            */}

                            <div className="ai-models-info-table__description-row">

                                <p className="ai-models-info-table__description ai-models-info-table__description--full-width">
                                    {model.description}
                                </p>

                            </div>

                        </React.Fragment>

                    ))}

                </div>
                
            </div>

            <div className="ai-models-info-table__mobile">

                {dataToRender.map((model) => (

                    <MobileModelCard
                        key={model.id}
                        model={model}
                    />

                ))}

            </div>

        </div>

    );

};



// Export component
export default AiModelsInfoTable;